/* eslint-disable max-lines */
// 9fbef606107a605d69c0edbcd8029e5d
/**
 * @module ProductItemComponents
 * Container of smaller function that will be renderer as Component to create a ProductItem.
 *
 * @author Florencia <facosta@minutentag.com>
 */
import React from 'react';
import PropTypes from 'prop-types';
import PriceCurrency from '@tcp/core/src/components/common/molecules/PriceCurrency';
import get from 'lodash/get';
import Notification from '@tcp/core/src/components/common/molecules/Notification';
import {
  routerPush,
  getBrand,
  getBrandInCaps,
  isClient,
  getIconPath,
  getLocator,
  isCanada,
  getSiteId,
  parseBoolean,
} from '@tcp/core/src/utils';
import { setSessionStorage, urlContainsQuery } from '@tcp/core/src/utils/utils.web';
import Image from '@tcp/core/src/components/common/atoms/Image';
import BodyCopy from '@tcp/core/src/components/common/atoms/BodyCopy';
import Anchor from '@tcp/core/src/components/common/atoms/Anchor';
import Button from '@tcp/core/src/components/common/atoms/Button';
import Col from '@tcp/core/src/components/common/atoms/Col';
import RichText from '@tcp/core/src/components/common/atoms/RichText';
// import { isClient, isTouchClient } from 'routing/routingHelper';
// import { isTouchClient } from '../../../../../../../utils';
import { isShopThisStoreModeOn } from '@tcp/core/src/reduxStore/selectors/session.selectors';
import {
  FAVORITE_ICON_STROKE,
  FAVORITE_ICON_FILL,
  PRODUCT_PRICE_BODY_COPY_COLOR,
} from '@tcp/core/src/constants/brandSpecific.constants';
import { ExtendedSizes } from '@tcp/core/src/components/features/browse/PlpTile/views/styles/ProductTitle.style';
import { getFormattedLoyaltyText, getProductListToPath } from '../utils/productsCommonUtils';
import ServerToClientRenderPatch from './ServerToClientRenderPatch';
import BundlePriceSection from './BundlePriceSection.view';
import { ProductMerchantPrice } from '../styles/CommonStyle';
import {
  getLabelFromCopyOrFromTranslation,
  renderLabel,
} from '../../../../../../../utils/labels/labelUtilty';
import {
  GLOBAL_ACTIONS_LABELS,
  GlobalActionsLabels,
  lblEdit,
} from '../../../../../../../utils/labels/globalActions.labels';
import {
  GLOBAL_GENERIC_LABELS,
  GlobalGenericLabels,
  lblGym,
  lblItems,
} from '../../../../../../../utils/labels/globalGeneric.labels';
import {
  FAVORITES_LABELS,
  FavoriteLabels,
  lblFavCreateNewList,
  lblFavMyFavWishList,
} from '../../../../Favorites/labels/Favorites.labels';

export function productLink(
  loadedProductCount,
  { to, asPath },
  event,
  alternateBrand,
  isSearchListing,
  values
) {
  const { isFromPLPPage, setCurrentCategoryIdActn, categoryIdToCheck } = values;
  event.preventDefault();
  if (isFromPLPPage) {
    setCurrentCategoryIdActn(categoryIdToCheck);
  }
  if (isClient()) {
    const skuClicked = event.target.closest('.item-container-inner').getAttribute('unbxdparam_sku');
    setSessionStorage({ key: 'LAST_PAGE_PATH', value: window.location.pathname });
    setSessionStorage({ key: 'LOADED_PRODUCT_COUNT', value: loadedProductCount });

    setSessionStorage({ key: 'SCROLL_POINT', value: window.pageYOffset });
    setSessionStorage({ key: 'UNBXDPARAM_SKU', value: skuClicked });
    setSessionStorage('LINK_HREF', window.scrollY);
    routerPush(to, asPath);
  }
}

const renderBrandTitle = (
  isShowBrandNameEnabled,
  tcpBrand,
  // gymBrand,
  snjBrand,
  pjpBrand,
  primaryBrand
) => {
  const globalGenericLabels = getLabelFromCopyOrFromTranslation(
    `${GLOBAL_GENERIC_LABELS}`,
    GlobalGenericLabels
  );
  const prodBrand = getBrand() && getBrand().toUpperCase();
  let brandTitle;
  if ((primaryBrand || prodBrand) === 'GYM') {
    brandTitle = renderLabel(globalGenericLabels[lblGym], 'lbl_gym');
  } else if ((primaryBrand || prodBrand) === 'TCP') {
    brandTitle = tcpBrand;
  } else if ((primaryBrand || prodBrand) === 'SNJ') {
    brandTitle = snjBrand;
  } else if ((primaryBrand || prodBrand) === 'PJP') {
    brandTitle = pjpBrand;
  }
  return isShowBrandNameEnabled ? (
    <BodyCopy
      fontSize="fs12"
      fontWeight="bold"
      fontFamily="secondary"
      className="product-brand-plp"
    >
      {brandTitle}
    </BodyCopy>
  ) : null;
};

const getFavoriteAsPath = (asPathNew, siteId) => {
  if (asPathNew.startsWith(siteId)) {
    return asPathNew.replace(siteId, '');
  }

  if (asPathNew.startsWith(`/${siteId}`)) {
    return asPathNew.replace(`/${siteId}`, '');
  }

  return asPathNew;
};

const renderBundleExtendedSizeCopy = ({
  isMountedTileRedesignAB,
  bundleProduct,
  extendedSizes,
  isSearchListing,
}) => {
  return isMountedTileRedesignAB && isSearchListing && bundleProduct && extendedSizes ? (
    <ExtendedSizes
      fontFamily="secondary"
      fontSize={['fs10', 'fs10', 'fs12']}
      fontWeight="extrabold"
    >
      {extendedSizes}
    </ExtendedSizes>
  ) : null;
};
const appendStsPartToUrl = (pdpAsUrl, stsPart) => {
  if (stsPart) {
    const separator = urlContainsQuery(pdpAsUrl) ? '&' : '?';
    return `${pdpAsUrl}${separator}${stsPart}`;
  }
  return pdpAsUrl;
};

const determineStsValues = (isSearchPage) => {
  const stsSlug =
    isShopThisStoreModeOn() && parseBoolean(isSearchPage) ? '&mode=shop-this-store' : '';
  const stsPart =
    isShopThisStoreModeOn() && parseBoolean(isSearchPage) ? 'mode=shop-this-store' : '';
  return { stsSlug, stsPart };
};

export function ProductTitle(values) {
  const {
    name,
    pdpUrl,
    loadedProductCount,
    children,
    isSearchPage,
    viaModule,
    isProductBrandOfSameDomain,
    page,
    isSoldOut,
    cookieToken,
    dataSource,
    googleAnalyticsData,
    alternateBrand,
    isShowBrandNameEnabled,
    primaryBrand,
    isSearchListing,
    isCrossBrandMerchandisingEnabled,
    isFavoriteView,
    badge2,
    isMountedTileRedesignAB,
    bundleProduct,
    TCPPrimaryCategory,
    useRouterNavigation,
  } = values;
  const labels = getLabelFromCopyOrFromTranslation(FAVORITES_LABELS, FavoriteLabels);
  const { stsSlug, stsPart } = determineStsValues(isSearchPage);

  const siteId = getSiteId();
  const pdpToPath =
    isProductBrandOfSameDomain || isCrossBrandMerchandisingEnabled
      ? getProductListToPath(pdpUrl)
      : pdpUrl;

  const pdpPageUrl =
    pdpToPath &&
    `${pdpToPath}&isSearchPage=${isSearchPage}&viaModule=${viaModule}&viaPage=${page}&dataSource=${dataSource}${
      TCPPrimaryCategory ? `&tcpPrimaryCategory=${TCPPrimaryCategory}` : ``
    }`;
  let pdpAsUrl = pdpUrl;
  const {
    tcpBrand = "The Children's Place",
    // gymBrand = 'Gymboree',
    snjBrand = 'Sugar & Jade',
    pjpBrand = 'PJ Place',
  } = labels;
  if (cookieToken && !isProductBrandOfSameDomain) {
    pdpAsUrl = `${pdpAsUrl}${urlContainsQuery(pdpAsUrl) ? '&' : '?'}ct=${cookieToken}`;
  }

  pdpAsUrl = appendStsPartToUrl(pdpAsUrl, stsPart);

  const toPath =
    alternateBrand && isSearchListing
      ? `${pdpPageUrl}$brand=${alternateBrand}${stsSlug}`
      : `${pdpPageUrl}${stsSlug}`;
  let asPathNew =
    alternateBrand && isSearchListing ? `${pdpAsUrl}$brand=${alternateBrand}` : `${pdpAsUrl}`;
  if (isFavoriteView) {
    asPathNew = getFavoriteAsPath(asPathNew, siteId);
  }

  return isSoldOut ? (
    <div className="product-title-container">
      <span inheritedStyles="product-title-content">
        {renderBrandTitle(
          isShowBrandNameEnabled,
          tcpBrand,
          // gymBrand,
          snjBrand,
          pjpBrand,
          primaryBrand
        )}
        {renderBundleExtendedSizeCopy({
          isMountedTileRedesignAB,
          bundleProduct,
          extendedSizes: badge2,
          isSearchListing,
        })}
        <BodyCopy
          fontSize={['fs12', 'fs13', 'fs14']}
          fontFamily="secondary"
          className="multiline-ellipsis"
        >
          {name}
        </BodyCopy>
      </span>
      {children}
    </div>
  ) : (
    <div className="product-title-container">
      <Anchor
        handleLinkClick={(e) => {
          googleAnalyticsData();
          productLink(
            loadedProductCount,
            { to: toPath, asPath: asPathNew },
            e,
            alternateBrand,
            isSearchListing,
            values
          );
        }}
        inheritedStyles="product-title-content"
        to={toPath}
        asPath={asPathNew}
        useRouterNavigation={useRouterNavigation}
      >
        {renderBrandTitle(
          isShowBrandNameEnabled,
          tcpBrand,
          // gymBrand,
          snjBrand,
          pjpBrand,
          primaryBrand
        )}
        {renderBundleExtendedSizeCopy({
          isMountedTileRedesignAB,
          bundleProduct,
          extendedSizes: badge2,
          isSearchListing,
        })}
        <BodyCopy
          fontSize={['fs12', 'fs13', 'fs14']}
          fontFamily="secondary"
          className="multiline-ellipsis"
        >
          {name}
        </BodyCopy>
      </Anchor>
      {children}
    </div>
  );
}

const isBundleorABTestSwitchOn = (bundleProduct, showPriceRange) =>
  !bundleProduct && !showPriceRange;

/* NOTE: This issue (DT-28867) added isMobile condition. */
/* NOTE: As per DT-29548, isMobile condition is not valid. "Offer" price should be shown below "List" price (always) */
/* NOTE: DT-27216, if offerPrice and listPrice are the same, just offerPrice should be shown (and will be black) */
export function ProductPricesSection(props) {
  const {
    listPrice,
    offerPrice,
    merchantTag,
    bundleProduct,
    priceRange,
    showPriceRange,
    isHideOriginalPrice,
    isTileRedesignAB,
    isFavoriteView,
    setPLPSLPAddToCartCTA,
    isTabbedModuleDiscountPriceDisabled,
  } = props;

  const highListPrice = priceRange && priceRange.highListPrice;
  const highOfferPrice = priceRange && priceRange.highOfferPrice;
  const lowListPrice = priceRange && priceRange.lowListPrice;
  const lowOfferPrice = priceRange && priceRange.lowOfferPrice;
  const brandId = getBrand();
  const classcheck = isTileRedesignAB && !isFavoriteView && setPLPSLPAddToCartCTA !== 'icon';
  const renderOfferPrice = () => {
    return (
      <>
        {offerPrice ? (
          <>
            <BodyCopy
              dataLocator={getLocator('global_Price_text')}
              color={PRODUCT_PRICE_BODY_COPY_COLOR[brandId.toUpperCase()]}
              fontWeight="extrabold"
              fontFamily="secondary"
              fontSize={['fs15', 'fs18', 'fs20']}
              className={classcheck ? 'main-price' : ''}
            >
              <PriceCurrency price={offerPrice} />
            </BodyCopy>
            {!isTabbedModuleDiscountPriceDisabled &&
              offerPrice !== listPrice &&
              !isHideOriginalPrice && (
                <ProductMerchantPrice isTileRedesignAB={isTileRedesignAB}>
                  <BodyCopy
                    component="span"
                    color="gray.700"
                    fontFamily="secondary"
                    fontWeight="semibold"
                    fontSize={['fs10', 'fs12', 'fs14']}
                    className="list-price"
                  >
                    <PriceCurrency price={listPrice} />
                  </BodyCopy>
                  {merchantTag && (
                    <BodyCopy
                      component="span"
                      color="red.500"
                      fontFamily="secondary"
                      fontWeight="semibold"
                      className="merchant-tag elem-ml-XXXS"
                      fontSize={['fs10', 'fs12', 'fs14']}
                    >
                      {merchantTag}
                    </BodyCopy>
                  )}
                </ProductMerchantPrice>
              )}
          </>
        ) : null}
      </>
    );
  };
  return (
    <>
      {isBundleorABTestSwitchOn(bundleProduct, showPriceRange) ? (
        <div className="container-price">
          {renderOfferPrice()}
          {merchantTag && !offerPrice && !isHideOriginalPrice && (
            <BodyCopy
              component="span"
              color="red.500"
              fontFamily="secondary"
              fontWeight="semibold"
              className="merchant-tag elem-ml-XXXS"
              fontSize={['fs10', 'fs12', 'fs14']}
            >
              {merchantTag}
            </BodyCopy>
          )}
        </div>
      ) : (
        BundlePriceSection(
          highListPrice,
          highOfferPrice,
          lowListPrice,
          lowOfferPrice,
          merchantTag,
          isHideOriginalPrice,
          bundleProduct,
          classcheck
        )
      )}
    </>
  );
}

/**
 * This Component Will be used to render inline search image svg in condensed header.
 * @param {*} classNamePrefix : this will be used as class name to change the color
 */
export const FavoritesIcons = ({ isSolidIcon }) => {
  const favIconStroke = FAVORITE_ICON_STROKE[getBrandInCaps()];
  const favIconFill = FAVORITE_ICON_FILL[getBrandInCaps()];
  if (isSolidIcon) {
    return (
      <div className="fav-heart-svg">
        <span // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: `
         <svg class="Layer_1" aria-hidden="true" focusable="false" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 27 27">
         <defs>
            <style>
            .solid-favorite-1{fill:${favIconFill};stroke:${favIconStroke};stroke-linecap:round;stroke-linejoin:round;}
          </style>
         </defs>
         <title>CHILDRENS PLACE-27X27</title>
           <path class="solid-favorite-1" d="M24.67,12.78A5.54,5.54,0,0,0,24.57,5h0a5.53,5.53,0,0,0-7.84,0L14.47,7.3a1.38,1.38,0,0,1-1.94,0L10.27,5A5.53,5.53,0,0,0,2.43,5h0a5.54,5.54,0,0,0-.1,7.74l10,10.33a1.59,1.59,0,0,0,2.28,0Z"/>
         </svg>`,
          }}
        />
      </div>
    );
  }
  return (
    <div className="fav-heart-svg">
      <span // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: `
        <svg class="Layer_1" aria-hidden="true"  focusable="false" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 27 27">
        <defs>
          <style>.empty-favorite-1{fill:none;stroke:#000;stroke-linecap:round;stroke-linejoin:round;}
          </style>
        </defs>
        <title>CHILDRENS PLACE-27X27</title>
          <path class="empty-favorite-1" d="M24.67,12.78A5.54,5.54,0,0,0,24.57,5h0a5.53,5.53,0,0,0-7.84,0L14.47,7.3a1.38,1.38,0,0,1-1.94,0L10.27,5A5.53,5.53,0,0,0,2.43,5h0a5.54,5.54,0,0,0-.1,7.74l10,10.33a1.59,1.59,0,0,0,2.28,0Z"/>
          <path class="empty-favorite-1" d="M2.92,8.18A2.66,2.66,0,0,1,5.58,5.52"/>
        </svg>`,
        }}
      />
    </div>
  );
};
FavoritesIcons.propTypes = {
  isSolidIcon: PropTypes.bool.isRequired,
};

export const FavoritesIconNew = ({ isSolidIcon, isFavPage }) => {
  const favIconStroke = FAVORITE_ICON_STROKE[getBrandInCaps()];
  const favIconFill = FAVORITE_ICON_FILL[getBrandInCaps()];
  if (isSolidIcon) {
    return (
      <span
        className={`fav-new-svg ${isFavPage ? 'svg-fav-page' : ''}`}
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: `
            <svg class="fav-svg-heart"  width="24" height="21" viewBox="0 0 23 21" xmlns="http://www.w3.org/2000/svg">
            <defs>
            <style>
            .solid-favorite-1{fill:${favIconFill};stroke:${favIconStroke};stroke-linecap:round;stroke-linejoin:round;}
          </style>
         </defs>
            <path class="solid-favorite-1"  d="M11.0684 3.42482L11.5035 4.16329L11.9322 3.4211C12.3792 2.64714 12.9172 2.0393 13.5913 1.55787L13.5928 1.55678C16.2912 -0.385654 20.0545 0.434486 21.7199 3.35943C22.6444 4.99165 22.6989 6.70756 22.1373 8.51607C21.7314 9.8111 21.0142 10.947 20.1609 12.0356L20.1602 12.0366C18.598 14.0396 16.7663 15.7915 14.8397 17.4817C14.0398 18.1828 13.2363 18.8786 12.433 19.5741C12.1214 19.8439 11.8099 20.1137 11.4986 20.3837C10.9568 19.9065 10.4146 19.4385 9.87485 18.9726C8.62924 17.8976 7.39643 16.8335 6.21177 15.6923L6.21094 15.6915C4.90718 14.4413 3.67435 13.1556 2.60578 11.7238C1.71479 10.5202 0.994462 9.27916 0.680707 7.85593C0.221806 5.72873 0.6181 3.86189 2.07372 2.28043C4.58521 -0.4379 8.9994 0.0215909 10.9376 3.21571L10.9421 3.22303L10.9467 3.23018C10.9837 3.28668 11.017 3.33747 11.0684 3.42482Z" fill="white" stroke="#1A1A1A"/>
            </svg>
            `,
        }}
      />
    );
  }

  return (
    <span
      className="fav-new-svg"
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{
        __html: `
          <svg class="fav-svg-heart"  width="24" height="21"  viewBox="0 0 23 21" fill="none" xmlns="http://www.w3.org/2000/svg">
          <defs>
          <style>.empty-favorite-1{fill:none;stroke:#000;stroke-linecap:round;stroke-linejoin:round;}
          </style>
        </defs>
          <path  class="empty-favorite-2" d="M11.0684 3.42482L11.5035 4.16329L11.9322 3.4211C12.3792 2.64714 12.9172 2.0393 13.5913 1.55787L13.5928 1.55678C16.2912 -0.385654 20.0545 0.434486 21.7199 3.35943C22.6444 4.99165 22.6989 6.70756 22.1373 8.51607C21.7314 9.8111 21.0142 10.947 20.1609 12.0356L20.1602 12.0366C18.598 14.0396 16.7663 15.7915 14.8397 17.4817C14.0398 18.1828 13.2363 18.8786 12.433 19.5741C12.1214 19.8439 11.8099 20.1137 11.4986 20.3837C10.9568 19.9065 10.4146 19.4385 9.87485 18.9726C8.62924 17.8976 7.39643 16.8335 6.21177 15.6923L6.21094 15.6915C4.90718 14.4413 3.67435 13.1556 2.60578 11.7238C1.71479 10.5202 0.994462 9.27916 0.680707 7.85593C0.221806 5.72873 0.6181 3.86189 2.07372 2.28043C4.58521 -0.4379 8.9994 0.0215909 10.9376 3.21571L10.9421 3.22303L10.9467 3.23018C10.9837 3.28668 11.017 3.33747 11.0684 3.42482Z" fill="white" stroke="#1A1A1A"/>
          </svg>
          `,
      }}
    />
  );
};
FavoritesIconNew.propTypes = {
  isSolidIcon: PropTypes.bool.isRequired,
};

export class ProductWishlistIcon extends ServerToClientRenderPatch {
  render() {
    const {
      onClick,
      isRemove,
      isDisabled,
      isMobile,
      className,
      activeButton,
      favoritedCount,
      isNewFavIcon,
      isFavPage,
      fromBuyItAgain,
      isMiniPDP,
      isPDPRedesignEnabled,
    } = this.props;
    const removeTextHeader = isMobile ? 'Tap to Remove' : 'Click to Remove';
    const removeTxtDesc = isMobile
      ? 'Remove this item from your Favorites List by tapping the heart icon again.'
      : 'Remove this item from your Favorites List by clicking the heart icon again.';

    return (
      <BodyCopy
        component="div"
        className="fav-icon-wrapper"
        onClick={onClick}
        isDisabled={isDisabled}
      >
        {isRemove ? (
          <div className="information-remove">
            <p className="information-remove-message">
              <strong className="remove-title">{removeTextHeader}</strong>
              <br />
              {removeTxtDesc}
            </p>
          </div>
        ) : (
          <>
            <button
              aria-label="Add To Favorite"
              title="Add To Favorite"
              className="clear-button"
              aria-pressed={activeButton ? 'true' : 'false'}
            >
              <span
                data-locator={getLocator('global_favorite_button')}
                className={activeButton ? `${className} active` : className}
              >
                {(isNewFavIcon && !fromBuyItAgain && !isFavPage) || isMiniPDP ? (
                  <FavoritesIconNew isSolidIcon={activeButton} isFavPage={isFavPage} />
                ) : (
                  <FavoritesIcons isSolidIcon={activeButton} />
                )}
              </span>
            </button>
            {!isPDPRedesignEnabled && (
              <BodyCopy
                dataLocator="pdp_favorite_icon_count"
                className="favorite-count"
                fontSize="fs10"
                fontWeight="regular"
                color="gray.600"
              >
                {favoritedCount}
              </BodyCopy>
            )}
          </>
        )}
      </BodyCopy>
    );
  }
}

export function BadgeItem(props) {
  const { text, className, isShowBadges, customFontWeight, customFontSize } = props;

  return (
    <div className={className}>
      <BodyCopy
        dataLocator={getLocator('global_productbadge_txt')}
        fontFamily="secondary"
        fontWeight={customFontWeight || 'semibold'}
        fontSize={customFontSize}
        className="product-badge"
      >
        {isShowBadges && text}
      </BodyCopy>
    </div>
  );
}

export function PromotionalMessage(props) {
  const { text, isInternationalShipping } = props;
  return text && !isInternationalShipping && !isCanada() ? (
    <BodyCopy
      fontSize={['fs9', 'fs12', 'fs14']}
      fontWeight="extrabold"
      fontFamily="secondary"
      data-locator={getLocator('global_loyalty_text')}
      className="loyalty-text-container"
      component="div"
    >
      {text && (
        <RichText
          className="rewards__benefits multiline-ellipsis"
          richTextHtml={getFormattedLoyaltyText(text)}
        />
      )}
    </BodyCopy>
  ) : null;
}

export function RedesignPDPPromotionalMessage(props) {
  const { text, isPlcc } = props;
  const unformattedText = getFormattedLoyaltyText(text);

  let icon = 'stars_plcc';
  if (isPlcc) {
    icon = 'blue_stars_plcc';
  }
  return (
    <div className="promotion-section-pdp-wrapper loyalty-promotion-banner">
      <div className="promotion-section-pdp-icon">
        <Image src={getIconPath(icon)} />
      </div>
      <div className="promotion-section-pdp-text loyalty-promotion-text">
        <span dangerouslySetInnerHTML={{ __html: unformattedText }} />
      </div>
      <div className="promotion-section-pdp-left-arrow">{''}</div>
    </div>
  );
}

const renderWishListItem = (item, activeWishListId) => (
  <div className="wish-list-item-section">
    <p className="wish-list-name">
      <span className={`${item.id === activeWishListId ? 'default-list' : ''}`}>
        {item.id === activeWishListId && (
          <Image
            src={getIconPath('selected-item-check-no-circle')}
            alt="check mark"
            className="wish-list-tick-mark icon-small"
            data-locator="wish-list-check-icon"
            height="20px"
          />
        )}
      </span>
      <span className={`${item.id === activeWishListId ? 'default-list-item' : 'other-list-item'}`}>
        {item.displayName}
      </span>
    </p>
    <p className="wish-list-count-section">
      <span
        className={`${
          item.id === activeWishListId ? 'default-list-count wish-list-count' : 'wish-list-count'
        }`}
      >
        {item.itemsCount}
      </span>
      <span>{` ${renderLabel(
        getLabelFromCopyOrFromTranslation(
          `${GLOBAL_GENERIC_LABELS}.${lblItems}`,
          GlobalGenericLabels
        ),
        'lbl_items'
      )}`}</span>
    </p>
  </div>
);

const moveToListErrorDisplay = (error) => {
  return error ? (
    <Notification status="error" colSize={{ large: 12, medium: 8, small: 6 }} message={error} />
  ) : null;
};

export const CreateWishList = (props) => {
  const labels = getLabelFromCopyOrFromTranslation(FAVORITES_LABELS, FavoriteLabels);

  const {
    wishlistsSummaries,
    // createNewWishList,
    createNewWishListMoveItem,
    itemId,
    getActiveWishlist,
    activeWishListId,
    openAddNewList,
    favoriteErrorMessages,
  } = props;
  const activateCreateButton = (wishlistsSummaries && wishlistsSummaries.length >= 5) || false;
  const errorMsg = favoriteErrorMessages
    ? get(favoriteErrorMessages[itemId], 'errorMessage', null)
    : '';
  return (
    <div className="create-wish-list-section">
      <h4 className="create-wish-list-header" dataLocator={getLocator('fav_list_lbl')}>
        {renderLabel(labels[lblFavMyFavWishList], 'lbl_fav_myFavWishList')}
      </h4>
      <ul>
        {wishlistsSummaries.map((item) => (
          <li className="wish-list-item">
            {createNewWishListMoveItem ? (
              <Button
                onClick={() => createNewWishListMoveItem({ wisListId: item.id, itemId })}
                className="wish-list-item__button"
              >
                {renderWishListItem(item, activeWishListId)}
              </Button>
            ) : (
              <Button
                onClick={() => {
                  getActiveWishlist(item.id, item.displayName);
                }}
                className="wish-list-change-item__button"
              >
                {renderWishListItem(item, activeWishListId)}
              </Button>
            )}
          </li>
        ))}
      </ul>
      {moveToListErrorDisplay(errorMsg)}
      <Button
        onClick={() => openAddNewList(itemId)}
        buttonVariation="fixed-width"
        fill="BLACK"
        data-locator="create-new-wish-list"
        className="create-new__button"
        disabled={activateCreateButton}
      >
        {renderLabel(labels[lblFavCreateNewList], 'lbl_fav_createNewList')}
      </Button>
    </div>
  );
};

export const ProductSKUInfo = (props) => {
  const { size, fit } = props;

  if (!size && !fit) {
    return null;
  }

  return (
    <div className="product-sku-info-container">
      {size && <span className="size-container">{`Size ${size}`}</span>}
      {size && fit && <span className="separator-bar-icon"> | </span>}
      {fit && <span className="fit-container">{fit}</span>}
    </div>
  );
};

export const PurchaseSection = (quantity, quantityPurchased) => {
  const labels = getLabelFromCopyOrFromTranslation(FAVORITES_LABELS, FavoriteLabels);

  return (
    !!quantity && (
      <div className="purchase-section">
        <span>
          {quantityPurchased}/{quantity}
        </span>
        <span className="is-purchase-label">
          {renderLabel(labels.lbl_fav_purchased, 'lbl_fav_purchased')}
        </span>
      </div>
    )
  );
};

export const WishListIcon = ({
  isFavoriteView,
  isInDefaultWishlist,
  handleAddRemoveFromWishlistObj,
  itemNotAvailable,
  favoritedCount,
  isSuggestedItem,
  labelsPlpTiles,
  isFavPage,
  isNewFavIcon,
  fromBuyItAgain,
  isMiniPDP,
  isSLP,
  isPDPRedesignEnabled,
}) => {
  if (itemNotAvailable && !isSuggestedItem) {
    return null;
  }

  return (
    <Col className={isSLP ? 'slp-favorite-icon' : ''} colSize={{ small: 2, medium: 2, large: 2 }}>
      <ProductWishlistIcon
        onClick={
          isInDefaultWishlist
            ? () => requestAnimationFrame(handleAddRemoveFromWishlistObj?.handleRemoveFromWishList)
            : () => requestAnimationFrame(handleAddRemoveFromWishlistObj?.handleAddToWishlist)
        }
        activeButton={isInDefaultWishlist || isFavoriteView}
        favoritedCount={favoritedCount}
        className={isNewFavIcon && !fromBuyItAgain && !isFavPage ? '' : 'fav-icon'}
        labelsPlpTiles={labelsPlpTiles}
        isNewFavIcon={isNewFavIcon}
        isFavPage={isFavPage}
        fromBuyItAgain={fromBuyItAgain}
        isMiniPDP={isMiniPDP}
        isPDPRedesignEnabled={isPDPRedesignEnabled}
      />
    </Col>
  );
};

export const EditButton = (props, selectedColorProductId, itemNotAvailable) => {
  // BRAND-SPECIFIC-TODO
  if (itemNotAvailable) {
    return null;
  }
  const { isFavoriteView, onQuickViewOpenClick, item } = props;
  if (isFavoriteView) {
    const {
      skuInfo: { skuId, size, fit, color },
    } = item;
    const { itemId, quantity, brand } = item.itemInfo;
    return (
      <Anchor
        className="edit-fav-item__button"
        handleLinkClick={(event) => {
          event.preventDefault();
          onQuickViewOpenClick({
            colorProductId: skuId,
            generalProductId: skuId,
            orderInfo: {
              orderItemId: itemId,
              selectedQty: quantity,
              selectedColor: color.name,
              selectedSize: size || '',
              selectedFit: fit || '',
              skuId,
              itemBrand: (brand || '').toUpperCase(),
            },
            isFavoriteEdit: true,
          });
        }}
        noLink
      >
        {renderLabel(
          getLabelFromCopyOrFromTranslation(
            `${GLOBAL_ACTIONS_LABELS}.${lblEdit}`,
            GlobalActionsLabels
          )
        )}
      </Anchor>
    );
  }
  return null;
};

EditButton.propTypes = {
  isFavoriteView: PropTypes.bool,
  onQuickViewOpenClick: PropTypes.func,
  item: PropTypes.shape({}),
};

EditButton.defaultProps = {
  isFavoriteView: false,
  onQuickViewOpenClick: () => {},
  item: {},
};

ProductSKUInfo.propTypes = {
  size: PropTypes.string,
  fit: PropTypes.string,
};

ProductSKUInfo.defaultProps = {
  size: '',
  fit: '',
};

CreateWishList.propTypes = {
  wishlistsSummaries: PropTypes.arrayOf({}).isRequired,
  // createNewWishList: PropTypes.func.isRequired,
  openAddNewList: PropTypes.func.isRequired,
  createNewWishListMoveItem: PropTypes.func.isRequired,
  itemId: PropTypes.string.isRequired,
  getActiveWishlist: PropTypes.func,
  activeWishListId: PropTypes.string.isRequired,
  favoriteErrorMessages: PropTypes.shape({}).isRequired,
};

CreateWishList.defaultProps = {
  getActiveWishlist: () => {},
};

PromotionalMessage.propTypes = {
  text: PropTypes.string.isRequired,
  isInternationalShipping: PropTypes.bool.isRequired,
};

RedesignPDPPromotionalMessage.propTypes = {
  text: PropTypes.string.isRequired,
  isPlcc: PropTypes.bool.isRequired,
};

BadgeItem.defaultProps = {
  text: '',
  className: '',
  customFontWeight: '',
  isShowBadges: true,
  customFontSize: ['fs10', 'fs12', 'fs14'],
};

BadgeItem.propTypes = {
  text: PropTypes.string,
  className: PropTypes.string,
  isShowBadges: PropTypes.bool,
  customFontWeight: PropTypes.string,
  customFontSize: PropTypes.arrayOf(PropTypes.string),
};

ProductPricesSection.defaultProps = {
  listPrice: 0,
  offerPrice: 0,
  merchantTag: '',
  bundleProduct: false,
  priceRange: {},
  showPriceRange: false,
  isTabbedModuleDiscountPriceDisabled: false,
};

ProductPricesSection.propTypes = {
  listPrice: PropTypes.number,
  offerPrice: PropTypes.number,
  merchantTag: PropTypes.string,
  bundleProduct: PropTypes.bool,
  priceRange: PropTypes.shape({}),
  showPriceRange: PropTypes.bool,
  isTabbedModuleDiscountPriceDisabled: PropTypes.bool,
};
